import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { KPICarousel, KPICard } from "../../../Components/KPICarousel";
import { getFleetBatteryHealthV2Kpis } from "../../../Api/BatteryHealthApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useBaseEntityInfo, usePagesContent } from "../../../Hooks/usePagesContent";

const SOHAndRULKPISection = ({ globalFilters = {}, vehiclesConnected, showHeader = true }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [kpiData, setKpiData] = useState([]);

    const { baseEntityTypeLable, baseEntityTypePluralLable } = useBaseEntityInfo();
    const entity = baseEntityTypeLable;           // e.g. "Battery" or "Vehicle"
    const entities = baseEntityTypePluralLable;   // e.g. "Batteries" or "Vehicles"

    const pageContent = usePagesContent();
    const commonConfig = pageContent?.metaInfo?.BatteryIntelligence?.commonConfig;
    const kpisConfig = pageContent?.metaInfo?.BatteryIntelligence?.SOHAndRUL?.kpis || {};

    const KPI_THRESHOLDS = {
        criticalSOHThreshold: commonConfig?.criticalSOHThreashold ?? 70,
        abnormalSOHDropThreshold: commonConfig?.sohDegradationRateThreasholdForCycles ?? 1.5,
    };
    const KPI_CONFIGURATIONS = {
        cycles: commonConfig?.sohDegradationRateCycles ?? 100,
        months: commonConfig?.sohDegradationRateMonths ?? 4,
        KMs:    commonConfig?.sohDegradationRateKm ?? 100,
    };

    useEffect(() => {
        const fetchKPIData = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await getFleetBatteryHealthV2Kpis(globalFilters);

                let apiData = {};
                if (res?.responseStatus?.code === CODE.SUCCESS || res?.responseStatus?.code === CODE.NODATA) {
                    apiData = res?.response?.data || {};
                } else {
                    throw new Error("Failed to fetch KPI data");
                }

                const isEnabled = (key) => kpisConfig[key]?.enabled === true;
                const getTitle = (key, defaultTitle) => kpisConfig[key]?.title || defaultTitle;

                const allKpis = [
                    {
                        configKey: "avgSOH",
                        title: getTitle("avgSOH", "Average SOH"),
                        titleTooltip: `Average State of Health of ${entities.toLowerCase()} across the fleet`,
                        value: `${Number(apiData.avgFleetSOH || 0).toFixed(2)}`,
                        unit: "%",
                        metrics: [
                            { label: `90th Percentile SOH`, value: `${Number(apiData.BestPerformingVehiclesSOH || 0).toFixed(2)}%` },
                            { label: `10th Percentile SOH`, value: `${Number(apiData.WorstPerformingVehiclesSOH || 0).toFixed(2)}%` },
                        ],
                    },
                    {
                        configKey: "criticalSOH",
                        title: getTitle("criticalSOH", `${entities} with critical SOH`),
                        titleTooltip: `Number of ${entities.toLowerCase()} whose State of Health has dropped below ${KPI_THRESHOLDS.criticalSOHThreshold}% SOH.`,
                        value: `${apiData.criticalVehiclesBelowThresholdSOH ?? 0}`,
                        unit: entities.toLowerCase(),
                        metrics: [
                            { label: `% of ${entities.toLowerCase()} with critical SOH`, value: `${Number(apiData.criticalVehiclesPercentageBelowThresholdSOH || 0).toFixed(1)}%` },
                        ],
                    },
                    {
                        configKey: "avgDegradationPerCycle",
                        title: getTitle("avgDegradationPerCycle", `Avg. SOH degradation rate per ${KPI_CONFIGURATIONS.cycles} cycles`),
                        titleTooltip: `Average SOH degradation rate per ${KPI_CONFIGURATIONS.cycles} cycles across the fleet.`,
                        value: `${Number(apiData.avgDegradationRatePerConfiguredCycles || 0).toFixed(2)}`,
                        unit: "%",
                        metrics: [],
                    },
                    {
                        configKey: "abnormalDropPerCycle",
                        title: getTitle("abnormalDropPerCycle", `Abnormal SOH drop per ${KPI_CONFIGURATIONS.cycles} cycles`),
                        titleTooltip: `Number of ${entities.toLowerCase()} that show an abnormal SOH drop per ${KPI_CONFIGURATIONS.cycles} cycles.`,
                        value: `${apiData.VehiclesWithAbnormalSOHDropPerConfiguredCyclesNumber ?? 0}`,
                        unit: entities.toLowerCase(),
                        metrics: [
                            { label: `% of ${entities.toLowerCase()}`, value: `${Number(apiData.VehiclesWithAbnormalSOHDropPerConfiguredCyclesPercentage || 0).toFixed(1)}%` },
                        ],
                    },
                    {
                        configKey: "abnormalDropPerKm",
                        title: getTitle("abnormalDropPerKm", `Abnormal SOH drop per ${KPI_CONFIGURATIONS.KMs} KMs`),
                        titleTooltip: `Number of ${entities.toLowerCase()} that show an abnormal SOH drop per ${KPI_CONFIGURATIONS.KMs} Kilometers.`,
                        value: `${apiData.VehiclesWithAbnormalSOHDropPerConfiguredKmsNumber ?? 0}`,
                        unit: entities.toLowerCase(),
                        metrics: [
                            { label: `% of ${entities.toLowerCase()}`, value: `${Number(apiData.VehiclesWithAbnormalSOHDropPerConfiguredKmsPercentage || 0).toFixed(1)}%` },
                        ],
                    },
                    {
                        configKey: "avgDegradationPerTimeRange",
                        title: getTitle("avgDegradationPerTimeRange", `Avg. SOH degradation rate per ${KPI_CONFIGURATIONS.months} months`),
                        titleTooltip: `Average SOH degradation rate per ${KPI_CONFIGURATIONS.months} months across the fleet.`,
                        value: `${Number(apiData.avgDegradationRatePerConfiguredTimeRange || 0).toFixed(2)}`,
                        unit: "%",
                        metrics: [],
                    },
                    {
                        configKey: "abnormalDropPerTimeRange",
                        title: getTitle("abnormalDropPerTimeRange", `Abnormal SOH drop per ${KPI_CONFIGURATIONS.months} months`),
                        titleTooltip: `Number of ${entities.toLowerCase()} that show an abnormal SOH drop per ${KPI_CONFIGURATIONS.months} months.`,
                        value: `${apiData.VehiclesWithAbnormalSOHDropPerConfiguredTimeRangeNumber ?? 0}`,
                        unit: entities.toLowerCase(),
                        metrics: [
                            { label: `% of ${entities.toLowerCase()}`, value: `${Number(apiData.VehiclesWithAbnormalSOHDropPerConfiguredTimeRangePercentage || 0).toFixed(1)}%` },
                        ],
                    },
                ];

                // org meta can set `maskValue: true` on a kpi to keep the card visible
                // but render its value (and metric values) as "-" regardless of API data.
                const isMasked = (key) => kpisConfig[key]?.maskValue === true;
                const visibleKpis = allKpis
                    .filter((kpi) => isEnabled(kpi.configKey))
                    .map((kpi) =>
                        isMasked(kpi.configKey)
                            ? { ...kpi, value: "-", unit: "", metrics: kpi.metrics.map((m) => ({ ...m, value: "-" })) }
                            : kpi
                    );
                setKpiData(visibleKpis);
            } catch (err) {
                console.error("Error fetching SOH KPI data:", err);
                setError(err?.message || "Failed to load KPI data");
            } finally {
                setLoading(false);
            }
        };

        fetchKPIData();
    }, [globalFilters]);

    const renderKPICard = (kpi) => (
        <KPICard
            title={kpi.title}
            titleTooltip={kpi.titleTooltip}
            value={kpi.value}
            unit={kpi.unit}
            metrics={kpi.metrics}
            totalVehicles={vehiclesConnected}
        />
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {showHeader && (
                <Box>
                    <Typography
                        variant="fw400s20c000"
                        sx={{ lineHeight: "28px", fontWeight: 300 }}
                    >
                        Key Performance Indicators
                    </Typography>
                </Box>
            )}

            <KPICarousel
                data={kpiData}
                renderCard={renderKPICard}
                loading={loading}
                error={error}
                autoWidthThreshold={0}
            />
        </Box>
    );
};

export default SOHAndRULKPISection;
